import React, { useContext } from 'react';
import { WebSite, WithContext } from 'schema-dts';

import Head from 'components/Head';

import { app, media } from 'context';
import { Layout } from 'ds';
import { OFFICE_REQUEST_PATH } from 'routes';

import AiOfficeAssistantVideo from './AiOfficeAssistantVideo';
import CTAs from './CTAs';
import FourTimesBetter from './FourTimesBetter';
import Hero from './Hero';
import Insights from './Insights';
import LogosMarquee from './LogosMarquee';
import OnePortalToRuleThemAll from './OnePortalToRuleThemAll';
import OneStopShop from './OneStopShop';
import Press from './Press';
import Services from './Services';
import ValueProps from './ValueProps';
import { HOME_PAGE_MAX_WIDTH } from './constants';

interface Props {}

const HOME_META_TITLE = 'Office for Rent | New York, San Francisco, Los Angeles';
const HOME_META_DESCRIPTION =
  'Private, move-in ready offices for rent. Fully furnished, managed office spaces with flexible terms in New York City, San Francisco Bay Area, and Los Angeles.';
const HOME_KEYWORDS =
  'office for rent, office for rent new york, office for rent san francisco, office for rent los angeles, office for lease, office space, flexible workspace, space rent, office space rent, flexible office spaces, office space new york, office space san francisco';

const Home: React.FC<Props> = () => {
  const { xs, isMobile } = useContext(media);
  const { contentPaddingX, windowWidth, contentWidth } = useContext(app);

  let usingMaxWidth = false;
  usingMaxWidth = windowWidth - contentPaddingX * 2 > HOME_PAGE_MAX_WIDTH;

  const containerProps = {
    maxWidth: usingMaxWidth ? HOME_PAGE_MAX_WIDTH : contentWidth + contentPaddingX * 2,
    flexGrow: 1,
    paddingX: usingMaxWidth ? undefined : contentPaddingX
  };

  const schema: WithContext<WebSite> = {
    '@context': 'https://schema.org',
    '@id': `${window.location.origin}/#website`,
    '@type': 'WebSite',
    name: 'Codi',
    url: window.location.origin,
    description: HOME_META_DESCRIPTION,
    keywords: HOME_KEYWORDS,
    hasPart: {
      '@type': 'WebPage',
      speakable: { '@type': 'SpeakableSpecification', xpath: ["/html/head/meta[@name='description']/@content"] },
      significantLink: `${window.location.origin}${OFFICE_REQUEST_PATH}`
    },
    publisher: {
      '@type': 'Corporation',
      name: 'Codi',
      legalName: 'Codi Inc.',
      logo: 'https://ik.imagekit.io/r2atwkj92wo/public/Codi-logo_icb15iqnP.png',
      image: 'https://ik.imagekit.io/r2atwkj92wo/public/tr:w-600/e6a421371e38c8d343831983605f0629_yu9zLLeag.jpeg',
      sameAs: [
        'https://www.linkedin.com/company/codiwork',
        'https://twitter.com/codiwork',
        'https://www.facebook.com/codicommunity',
        'https://www.instagram.com/codiwork/',
        'https://www.crunchbase.com/organization/codi-2000'
      ],
      brand: {
        '@type': 'Brand',
        name: 'Codi'
      },
      funder: [
        {
          '@type': 'Corporation',
          '@id': 'https://a16z.com/',
          legalName: 'AH Capital Management, LLC',
          name: 'Andreessen Horowitz',
          url: 'https://a16z.com/',
          sameAs: 'https://en.wikipedia.org/wiki/Andreessen_Horowitz'
        }
      ]
    }
  };

  return (
    <>
      <Layout justify="center" color="blue-25" position="relative">
        <Layout {...(xs ? {} : containerProps)}>
          <Head title={HOME_META_TITLE} description={HOME_META_DESCRIPTION} keywords={HOME_KEYWORDS} />
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
          <Hero />
          <OnePortalToRuleThemAll />
          <AiOfficeAssistantVideo />
        </Layout>
      </Layout>
      <Layout color="blue-25" paddingTop={xs ? 128 : 64} paddingBottom={128}>
        <LogosMarquee />
      </Layout>
      <Layout justify="center" color="blue-25" position="relative">
        <Layout {...(xs ? {} : containerProps)} color="blue-25">
          <OneStopShop />
          <FourTimesBetter />
          <ValueProps />
          <Services />
          <Layout marginY={isMobile ? undefined : 100}>
            <Press />
          </Layout>
        </Layout>
      </Layout>
      <Layout
        {...(isMobile
          ? { paddingX: contentPaddingX, color: 'blue-25', paddingY: 64 }
          : { color: 'blue-50', paddingTop: 92, paddingY: 100 })}
        justify="center"
      >
        <Layout {...(xs ? {} : containerProps)}>
          <Insights />
          <Layout marginTop={isMobile ? 64 : 100}>
            <CTAs />
          </Layout>
        </Layout>
      </Layout>
    </>
  );
};

export default Home;
